import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { ITankProfile } from '../interfaces/tankProfile';
import { ITankProfileChangeRequest } from '../interfaces/tankProfile';
import { DSRESTClient } from '../shared/communication-objects/restclient.service';
import { LogService } from './log.service';
import { ITankGridColumn } from 'src/app/interfaces/tankGridColumn';
import {
  DeleteTankProfileResultDto,
  DeleteTankProfileResultEnum,
} from '../interfaces/deleteTankProfileResponseDto';
import { Cacheable, CacheBuster } from 'ts-cacheable';
import { TapAppStore } from '@state/tap-state';
import { selectLastSelectedTankProfileId } from '@state/selectors/tank-profile.selectors';
import { AppDialogService } from './app-dialog.service';
import { TranslateService } from '@ngx-translate/core';
import * as TankProfileActions from '@state/actions/tank-profile.actions';
import { DeleteFailureModalComponent } from '@components/delete-failure-modal/delete-failure-modal.component';
import { selectUserInformation } from '@state/selectors/user-profile.selectors';
import { AuthService } from './auth.service';
import { SecurityPolicies } from './auth-variables';
import { AppSettings } from '../shared/Constants/sharedConstants';
import { cloneDeep } from 'lodash';
import { FilterProfileService } from './filter-profile.service';

@Injectable()
export class TankProfileService {
  private readonly apiURL: string = 'TankProfile';

  private tankProfilesCache: {
    data: ITankProfile[] | null;
    timestamp: number;
    allowSlidingScale: boolean;
  } = {
    data: null,
    timestamp: 0,
    allowSlidingScale: true,
  };

  constructor(
    private restClient: DSRESTClient,
    private logger: LogService,
    private tapAppStore: TapAppStore,
    private appDialogService: AppDialogService,
    private translate: TranslateService,
    private authService: AuthService,
    private _fps: FilterProfileService
  ) {}

  @CacheBuster({ isInstant: true })
  bustCache() {
    this.tankProfilesCache = {
      data: null,
      timestamp: 0,
      allowSlidingScale: true,
    };
  }

  getTankProfiles(): Observable<ITankProfile[]> {
    return of(null).pipe(
      map(() => this.getTankProfilesFromCache()),
      switchMap(cachedProfiles => {
        if (cachedProfiles) {
          return of(cachedProfiles);
        }

        return combineLatest([
          this.tapAppStore.store.select(selectUserInformation).pipe(filter(user => !!user)),
          this.authService.passesPolicyOf(SecurityPolicies.RequiresManageTanks),
          this.getTankProfilesFromServer(),
        ]).pipe(
          tap(response => this.logger.log('GetUserTankProfiles Response', response)),
          map(([userInfo, hasManageTankProfiles, profiles]) => {
            return (profiles ?? []).map(profile =>
              this.addReadOnlyFlagToProfile(profile, userInfo, hasManageTankProfiles)
            );
          }),
        tap(profiles => {
          // Update cache
          this.tankProfilesCache.data = profiles;
          const now = Date.now();
          this.tankProfilesCache.timestamp = now;
        }),
        );
      })
    );
  }

  getTankProfilesFromServer(): Observable<ITankProfile[]> {
    return this.restClient.getDirect<ITankProfile[]>(`${this.apiURL}/GetUserTankProfiles`);
  }

  getTankProfile(tankProfileId: number): Observable<ITankProfile | null> {
    let apiResponse = combineLatest([
      this.tapAppStore.store.select(selectUserInformation).pipe(filter(user => !!user)),
      this.authService.passesPolicyOf(SecurityPolicies.RequiresManageTanks),
      this.restClient.getAsModel<ITankProfile>(`${this.apiURL}/GetTankProfile/${tankProfileId}`),
    ]).pipe(
      //one profile, then complete. Without this the store selectors keep the combineLatest alive and it
      //re-emits on every user-information change (Apply causes one), re-running every caller's handler -
      //the editor reloaded and dropped unsaved edits, and its finalize/loading state never settled
      take(1),
      tap(response => this.logger.log('GetTankProfile Response', response)),
      map(([userInfo, hasManageTankProfiles, profile]) =>
        this.addReadOnlyFlagToProfile(profile, userInfo, hasManageTankProfiles)
      )
    );
    return apiResponse;
  }

  changeActiveTankProfileTo(changeRequest: ITankProfileChangeRequest): Observable<any> {
    this.bustCache();
    let apiResponse = this.restClient.post(
      `${this.apiURL}/ChangeActiveTankProfileTo`,
      changeRequest
    );
    return apiResponse.pipe(
      tap(response => this.logger.log('ChangeTankProfile Response', response))
    );
  }

  createTankProfile(tankProfile: ITankProfile): Observable<any> {
    this.bustCache();
    let apiResponse = this.restClient.post(`${this.apiURL}/CreateTankProfile`, tankProfile);
    return apiResponse.pipe(
      tap(response => this.logger.log('CreateTankProfile Response', response))
    );
  }

  copyTankProfile(tankProfileId: number, isTVP: boolean = false): Observable<ITankProfile> {
    this.bustCache();
    this.appDialogService.showGeneralMessage('Copying Tank Profile', {
      duration: 2000,
    });
    //isTVP tells the API which id space tankProfileId belongs to - TankProfile or TankViewProfile
    let apiResponse = this.restClient.postForResponse<ITankProfile>(
      `${this.apiURL}/copy/${tankProfileId}?isTVP=${isTVP}`,
      null
    );
    return apiResponse
      .pipe(tap(response => this.logger.log('CopyTankProfile Response', response)))
      .pipe(
        tap(response => {
          if (!response) {
            this.appDialogService.showErrorMessage(
              this.translate.instant('TankProfiles.FailedToCopy')
            );
            return;
          }

          this.appDialogService.showSuccessMessage(
            this.translate.instant('TankProfiles.CopiedSuccessfully')
          );
          //the copy endpoint already persisted the profile - applying it is the caller's job,
          //so it can respect unsaved edits (see TankProfilesComponent.onCopyProfile)
        })
      );
  }

  saveTankProfile(tankProfile: ITankProfile): Observable<any> {
    this.bustCache();
    
    //filter for only selected columns to send in the payload
    const payload = cloneDeep(tankProfile);
    payload.tankGridColumns = tankProfile.tankGridColumns.filter(tgc => tgc.isSelected === true);
    
    this.warnUserIfSkytankFilterActive();

    let apiResponse = this.restClient.post(`${this.apiURL}/SaveTankProfile`, tankProfile);
    return apiResponse.pipe(
      tap({
        next: response => {
          if (typeof response === 'string') {
            this.appDialogService.showErrorMessage(response);
            return;
          }
          this.logger.log('SaveTankProfile Response', response);
          this.appDialogService.showSuccessMessage(this.translate.instant('TankProfiles.Saved'));
        },
      }),
      catchError(error => {
        this.appDialogService.showErrorMessage(this.translate.instant('TankProfiles.SaveFailed'));
        return of(null);
      })
    );
  }

  warnUserIfSkytankFilterActive() {
    this._fps.warnUserIfSkytankFilterActive();
  }
  
  @Cacheable()
  getUserTankGridColumns(tankProfileId: number): Observable<any> {
    let apiResponse = this.restClient.get<ITankGridColumn[]>(
      `${this.apiURL}/getUserTankGridColumns/${tankProfileId}`
    );
    return apiResponse.pipe(
      tap(response => this.logger.log('getUserTankGridColumns Response', response))
    );
  }

  deleteTankProfile(tankProfile: ITankProfile): Observable<DeleteTankProfileResultDto> {
    this.bustCache();
    let message = this.translate.instant('TankProfiles.DeleteConfirm');
    message += ` "${tankProfile.tankProfileName}"?`;

    return this.appDialogService
      .showConfirmationDialog(this.translate.instant('TankProfile.DeleteConfirmation'), message)
      .pipe(
        switchMap(confirmed => {
          if (confirmed) {
            return this.restClient.deleteForResponse<DeleteTankProfileResultDto>(
              `${this.apiURL}/delete/${tankProfile.tankProfileId}`
            );
          }
          return of(null);
        }),
        take(1),
        tap({
          next: (result: DeleteTankProfileResultDto) => {
            if (!result) return;

            if (result.status === DeleteTankProfileResultEnum.Success) {
              this.appDialogService.showSuccessMessage(
                this.translate.instant('TankProfiles.DeleteSuccess')
              );

              this.switchToNextTankProfileIfCurrent(tankProfile.tankProfileId).subscribe(
                nextProfile => {
                  this.tapAppStore.store.dispatch(
                    new TankProfileActions.DeleteTankProfile(tankProfile.tankProfileId)
                  );
                  this.logger.log('Tank Profile Deleted', tankProfile.tankProfileId);
                }
              );
            } else {
              this.appDialogService.showDialog(DeleteFailureModalComponent, result);
            }
          },
          error: error => {
            this.appDialogService.showErrorMessage(
              this.translate.instant('TankProfiles.DeleteFailed')
            );
          },
        })
      );
  }

  tankProfileNameAlreadyExists(
    tankProfileName: string,
    tankProfileId?: number
  ): Observable<boolean> {
    const tpnl = tankProfileName?.trim()?.toLowerCase();
    return this.getTankProfiles().pipe(
      map(profiles => {
        if (!tankProfileId) return false;
        const tp = profiles.find(profile => profile.tankProfileId === tankProfileId);
        //the profile being edited may not be in the list yet (freshly created/copied, or filtered out)
        if (!tp) return false;
        return profiles.some(
          profile =>
            profile.tankProfileName?.trim()?.toLowerCase() === tpnl &&
            profile.tankProfileId !== tankProfileId &&
            profile.userId === tp.userId
        );
      }),
      tap(result => this.logger.log('Tank Profile Name Already Exists', result))
    );
  }

  tankProfileHasDependents(tankProfileId: number): Observable<boolean> {
    const apiResponse = this.restClient.getDirect<boolean>(
      `${this.apiURL}/tank-profile-has-dependents/${tankProfileId}`
    );
    return apiResponse.pipe(
      tap(response => this.logger.log('Tank Profile Has Dependents', response)),
      map(response => response)
    );
  }

  tankProfileHasOtherUsersAttached(tankProfileId: number): Observable<boolean> {
    const apiResponse = this.restClient.getDirect<boolean>(
      `${this.apiURL}/tank-profile-has-other-users/${tankProfileId}`
    );
    return apiResponse.pipe(
      tap(response => this.logger.log('Tank Profile Has Other Users Attached', response)),
      map(response => response)
    );
  }

  showTankProfileHasDependentsError() {
    this.appDialogService.showErrorMessage(
      'This tank profile has dependent reports and cannot be deleted.'
    );
  }

  private addReadOnlyFlagToProfile(
    profile: ITankProfile,
    userInfo: any,
    hasManageTankProfiles: boolean
  ): ITankProfile {
    return {
      ...profile,
      isPersonal: profile.userId === userInfo.userId,
      isReadOnly:
        profile.isReadOnly ||
        (!profile.isPrivate && profile.userId !== userInfo.userId && !hasManageTankProfiles),
    };
  }

  private switchToNextTankProfileIfCurrent(tankProfileId: number) {
    this.bustCache();
    const userId$ = this.tapAppStore.getCurrentUserInformation$();
    const lastSelectedTankProfileId$ = this.tapAppStore.store.select(
      selectLastSelectedTankProfileId
    );
    const currentTankProfiles$ = this.getTankProfiles();

    return lastSelectedTankProfileId$.pipe(
      switchMap(lastSelectedId => {
        // If the last selected profile is the one being deleted, find the next available profile
        //only use the User's own tank profiles
        if (lastSelectedId !== tankProfileId) return of(null);

        return userId$.pipe(
          switchMap(user => {
            if (!user) return of(null);
            // Get the current tank profiles
            return currentTankProfiles$.pipe(
              take(1),
              map(profiles => {
                const userProfiles = profiles.filter(p => p.userId === user.userId);
                const currentIndex = userProfiles.findIndex(p => p.tankProfileId === tankProfileId);
                const nextProfile =
                  userProfiles[currentIndex + 1] || userProfiles[currentIndex - 1];
                return nextProfile;
              })
            );
          })
        );
      }),

      take(1),

      switchMap(nextProfile => {
        if (!nextProfile) return of(null);

        this.tapAppStore.store.dispatch(
          new TankProfileActions.ActiveTankProfileChanged(nextProfile)
        );
        return of(nextProfile);
      })
    );
  }

  private getTankProfilesFromCache(): ITankProfile[] | null {
    const now = Date.now();
    if (this.isCacheValid(now)) {
      this.logger.log('Using cached tank profiles', this.tankProfilesCache.data);
      return this.tankProfilesCache.data;
    } else {
      this.logger.log('Tank profiles cache is invalid or expired');
      return null;
    }
  }

  private isCacheValid(now: number) {
    return (
      this.tankProfilesCache.data &&
      now - this.tankProfilesCache.timestamp < AppSettings.maxCacheAgeShorter
    );
  }
}
